import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Paper,
    Grid,
    Alert,
    CircularProgress,
    Breadcrumbs,
    Link,
} from '@mui/material';
import { ArrowBack, Save, Info, Description, Gavel } from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { getCmsByType, updateCms, createCms } from '../../services/Api';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const ModifyCms = () => {
    const { type } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const [formData, setFormData] = useState({
        type: type || 'about_us',
        content: '',
    });

    useEffect(() => {
        // 1. Try to get data from navigation state
        if (location.state?.cmsData) {
            setFormData({
                type: type || 'about_us',
                content: location.state.cmsData.content || '',
            });
            return;
        }

        // 2. Fallback to API if type is present
        if (type) {
            const fetchCms = async () => {
                setLoading(true);
                try {
                    const response = await getCmsByType(type);
                    if (response.success) {
                        setFormData({
                            type: type,
                            content: response.data.content || '',
                        });
                    } else {
                        setError(`Failed to fetch ${type.replace(/_/g, ' ')} content`);
                    }
                } catch (err) {
                    setError(`An error occurred while fetching ${type.replace(/_/g, ' ')} content`);
                    console.error(err);
                } finally {
                    setLoading(false);
                }
            };
            fetchCms();
        }
    }, [type, location.state]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const strippedContent = (formData.content || '').replace(/(<([^>]+)>)/gi, "").replace(/&nbsp;/gi, "").trim();
        if (!strippedContent) {
            setError('invalid content');
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(false);

        try {
            let response;
            const submitData = {
                type: formData.type,
                content: formData.content
            };

            if (type) {
                // Edit mode: id/type is in URL
                response = await updateCms(type, submitData);
            } else {
                // Create mode
                response = await createCms(submitData);
            }

            if (response.success) {
                setSuccess(true);
                setTimeout(() => navigate('/cms'), 1500);
            } else {
                setError(response.message || `Failed to ${type ? 'update' : 'create'} ${formData.type.replace(/_/g, ' ')} content`);
            }
        } catch (err) {
            setError(err.response?.data?.message || `An error occurred while ${type ? 'updating' : 'creating'} the content`);
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['link', 'clean']
        ],
    };

    return (
        <Box sx={{ p: 0 }}>
            <Box sx={{ mb: 3 }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
                    <Link
                        underline="hover"
                        color="inherit"
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            navigate('/cms');
                        }}
                        sx={{ display: 'flex', alignItems: 'center' }}
                    >
                        <ArrowBack sx={{ mr: 0.5, fontSize: 'inherit' }} />
                        CMS
                    </Link>
                    <Typography color="text.primary">{type ? 'Edit Content' : 'Create New Content'}</Typography>
                </Breadcrumbs>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {type ? `Modify ${type.replace(/_/g, ' ')}` : 'Create CMS Content'}
                </Typography>
            </Box>

            <Paper elevation={0} sx={{ p: 4, borderRadius: 2, border: '1px solid', borderColor: 'text.border' }}>
                {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 3 }}>Content updated successfully! Redirecting...</Alert>}

                <form onSubmit={handleSubmit}>
                    <Grid container spacing={4}>
                        <Grid size={{ xs: 12, md: 8 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                <Box sx={{ mb: 1 }}>
                                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                                        Page Content
                                    </Typography>
                                    <Box sx={{ 
                                        '& .ql-container': { height: '500px', borderRadius: '0 0 8px 8px' },
                                        '& .ql-toolbar': { borderRadius: '8px 8px 0 0' },
                                        '& .ql-editor': { fontSize: '1rem', fontFamily: 'inherit' }
                                    }}>
                                        <ReactQuill
                                            theme="snow"
                                            value={formData.content}
                                            onChange={(content) => setFormData({ ...formData, content: content })}
                                            modules={quillModules}
                                            placeholder="Write your content here..."
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                <FormControl fullWidth variant="outlined" disabled={!!type}>
                                    <InputLabel id="cms-type-label">Select Content Type</InputLabel>
                                    <Select
                                        labelId="cms-type-label"
                                        id="cms-type"
                                        value={formData.type}
                                        label="Select Content Type"
                                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                                    >
                                        <MenuItem value="about_us">
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Info fontSize="small" /> About Us
                                            </Box>
                                        </MenuItem>
                                        <MenuItem value="terms_and_conditions">
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Gavel fontSize="small" /> Terms & Conditions
                                            </Box>
                                        </MenuItem>
                                        <MenuItem value="privacy_policy">
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Description fontSize="small" /> Privacy Policy
                                            </Box>
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                                
                                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        fullWidth
                                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Save />}
                                        disabled={loading}
                                        sx={{
                                            backgroundColor: 'primary.main',
                                            '&:hover': { backgroundColor: 'primary.dark' },
                                            py: 1.5,
                                            fontWeight: 600,
                                        }}
                                    >
                                        Save Content
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        onClick={() => navigate('/cms')}
                                        disabled={loading}
                                        sx={{ py: 1.2 }}
                                    >
                                        Cancel
                                    </Button>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </form>
            </Paper>
        </Box>
    );
};

export default ModifyCms;
